import {useQueryClient} from "@tanstack/react-query";
import {Clock} from "lucide-react";
import {useCallback, useEffect, useRef, useState} from "react";
import {useForm} from "react-hook-form";

import {Button} from "@/components/ui/button";
import {Dialog, DialogContent} from "@/components/ui/dialog";
import {Form, FormControl, FormField, FormItem, FormLabel} from "@/components/ui/form";
import FormError from "@/components/ui/form-error";
import {Input} from "@/components/ui/input";
import {PatrolVehicleConditionDialog} from "@/src/modules/company-vehicles/components";
import {shouldPromptPatrolEndConditionCheck} from "@/src/modules/company-vehicles/lib/patrol-vehicle-condition";
import {getCurrentShiftCompanyVehicle} from "@/src/modules/company-vehicles/lib/shift-company-vehicles";
import {
  ComplianceGateActionButton,
  ComplianceGateActionError,
  ComplianceGateDialogHeader,
  ComplianceReconcileOverlay,
  CoordinatesRequiredGate,
  complianceGateDialogContentClassName,
} from "@/src/modules/core/components";
import {useToast} from "@/src/modules/core/hooks";
import {useBlockingGate} from "@/src/modules/core/hooks/use-blocking-gate";
import {
  useComplianceGateCoordinates,
  prewarmGateCoordinatesOnPointerDown,
} from "@/src/modules/core/hooks/use-compliance-gate-coordinates";
import {moment} from "@/src/modules/core/lib";
import {resolveShiftGpsForSubmit} from "@/src/modules/core/lib/resolve-shift-gps-coordinates";
import {reconcileCompliance} from "@/src/modules/core/lib/reconcile-compliance";
import {triggerNotificationAttention} from "@/src/modules/notification-feed/lib/notification-attention";
import {beginComplianceGateHandling} from "@/src/modules/notification-feed/lib/dispatch-shift-compliance-gate-from-notification";
import {useSetBlockingGateReconciling} from "@/src/modules/core/store/blocking-gate";
import {Shift} from "@/src/modules/shifts/types";

import {
  SHIFT_END_ALERT_EVENT_NAMES,
  subscribeWindowEvents,
} from "../../lib/compliance-realtime-events";
import {deriveShiftGatePresence} from "../../lib/compliance-gates";
import {finalizeShiftEndSession} from "../../lib/finalize-shift-end-session";
import {
  getShiftGateErrorTitle,
  isShiftGateBusinessError,
} from "../../lib/shift-gate-errors";
import {useActiveShift, useExtendShift, useShiftEnd} from "../../hooks";

type ShiftGateAction = "stay" | "end";

type PatrolEndFlow = {
  shiftId: number;
  lockedVehicle: {id: number; name?: string | null} | null;
};

function readActiveShiftFromCache(queryClient: ReturnType<typeof useQueryClient>) {
  return queryClient.getQueryData<{shift: Shift | null}>(["active-shift"])?.shift ?? null;
}

export function ShiftEndAlert() {
  const queryClient = useQueryClient();
  const shiftEndRevokedRef = useRef(true);
  const {shift: activeShift} = useActiveShift();
  const [patrolEndFlow, setPatrolEndFlow] = useState<PatrolEndFlow | null>(null);

  const toast = useToast();
  const {mutateAsync: extendShift, isPending: isExtendingShift} = useExtendShift();
  const {mutateAsync: shiftEnd, isPending: isShiftingEnded} = useShiftEnd();
  const setReconciling = useSetBlockingGateReconciling();
  const [endShiftBusy, setEndShiftBusy] = useState(false);
  const [actionError, setActionError] = useState<{
    message: string;
    action: ShiftGateAction;
  } | null>(null);

  const present =
    patrolEndFlow == null && deriveShiftGatePresence({shift: activeShift}).shiftEnd;

  const {isVisible, showReconcilingOverlay} = useBlockingGate({id: "shift-end", present});

  useComplianceGateCoordinates(present || patrolEndFlow != null);

  const lastAttentionKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isVisible || !activeShift?.shift_id) {
      return;
    }
    const attentionKey = `shift-end-${activeShift.shift_id}`;
    if (lastAttentionKeyRef.current === attentionKey) {
      return;
    }
    lastAttentionKeyRef.current = attentionKey;
    triggerNotificationAttention("shift.end_alert", attentionKey);
  }, [isVisible, activeShift?.shift_id]);

  useEffect(() => {
    function handleShiftAutocompleted() {
      void (async () => {
        setReconciling(true, "shift-end");
        try {
          await reconcileCompliance(queryClient);
          setPatrolEndFlow(null);
        } finally {
          setReconciling(false, "shift-end");
        }
      })();
    }

    window.addEventListener("shift:autocompleted", handleShiftAutocompleted);
    return () => {
      window.removeEventListener("shift:autocompleted", handleShiftAutocompleted);
    };
  }, [queryClient, setReconciling]);

  useEffect(() => {
    function readShiftEndAlertShiftId(detail: unknown): number | undefined {
      const record =
        detail && typeof detail === "object" && !Array.isArray(detail)
          ? (detail as Record<string, unknown>)
          : null;
      if (!record) {
        return undefined;
      }
      const id = record.shift_id;
      if (typeof id === "number" && Number.isFinite(id)) {
        return id;
      }
      if (typeof id === "string") {
        const parsed = Number(id);
        return Number.isFinite(parsed) ? parsed : undefined;
      }
      return undefined;
    }

    function handleShiftEnd(event: Event) {
      if (patrolEndFlow) return;
      const eventShiftId = readShiftEndAlertShiftId((event as CustomEvent<unknown>).detail);
      if (
        eventShiftId != null &&
        !beginComplianceGateHandling("shift-end", eventShiftId)
      ) {
        return;
      }
      const shiftId = eventShiftId ?? activeShift?.shift_id;
      if (shiftId != null) {
        triggerNotificationAttention("shift.end_alert", `shift-end-${shiftId}`);
      }
      void (async () => {
        setReconciling(true, "shift-end");
        try {
          await reconcileCompliance(queryClient);
        } finally {
          setReconciling(false, "shift-end");
        }
      })();
    }

    return subscribeWindowEvents(SHIFT_END_ALERT_EVENT_NAMES, handleShiftEnd);
  }, [activeShift?.shift_id, queryClient, patrolEndFlow, setReconciling]);

  const handleStay = async (extensionMinutes: number) => {
    if (!activeShift) return;
    setActionError(null);
    try {
      await extendShift({shift_id: activeShift.shift_id, extension_minutes: extensionMinutes});
      toast.success("Shift extended successfully");
      setReconciling(true, "shift-end");
      try {
        await reconcileCompliance(queryClient, {shiftId: activeShift.shift_id});
      } finally {
        setReconciling(false, "shift-end");
      }
    } catch (e) {
      if (!isShiftGateBusinessError(e)) return;
      setActionError({message: getShiftGateErrorTitle(e), action: "stay"});
    }
  };

  const finalizeShiftEndSessionCallback = useCallback(() => {
    finalizeShiftEndSession(shiftEndRevokedRef.current, queryClient);
  }, [queryClient]);

  const handleEndOfShift = useCallback(async () => {
    if (!activeShift) return;
    setActionError(null);
    setEndShiftBusy(true);
    try {
      const gps = await resolveShiftGpsForSubmit(activeShift, "shift_end");
      if (!gps) {
        return;
      }
      const result = await shiftEnd({
        shift_id: activeShift.shift_id,
        gps_coordinates: gps.gps_coordinates,
        gps_source: gps.gps_source,
      });
      shiftEndRevokedRef.current = result.tokens_revoked;
      toast.success("Shift ended successfully");
      setReconciling(true, "shift-end");
      try {
        await reconcileCompliance(queryClient, {shiftId: activeShift.shift_id});
      } finally {
        setReconciling(false, "shift-end");
      }
      finalizeShiftEndSessionCallback();
    } catch (e) {
      if (!isShiftGateBusinessError(e)) return;
      setActionError({message: getShiftGateErrorTitle(e), action: "end"});
    } finally {
      setEndShiftBusy(false);
    }
  }, [activeShift, finalizeShiftEndSessionCallback, setReconciling, shiftEnd, toast]);

  const requestEndFromAlert = useCallback(() => {
    if (!activeShift) return;
    if (shouldPromptPatrolEndConditionCheck(activeShift)) {
      setPatrolEndFlow({
        shiftId: activeShift.shift_id,
        lockedVehicle: getCurrentShiftCompanyVehicle(activeShift),
      });
      return;
    }
    void handleEndOfShift();
  }, [activeShift, handleEndOfShift]);

  return (
    <>
      {isVisible && activeShift ? (
        <ShiftEndDialog
          open
          shift={activeShift}
          isShiftingEnded={isShiftingEnded}
          isExtendingShift={isExtendingShift}
          endShiftBusy={endShiftBusy}
          showReconcilingOverlay={showReconcilingOverlay}
          actionError={actionError}
          onClearActionError={() => setActionError(null)}
          onStay={handleStay}
          onEndOfShift={requestEndFromAlert}
        />
      ) : null}
      {patrolEndFlow ? (
        <PatrolVehicleConditionDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setPatrolEndFlow(null);
              void (async () => {
                setReconciling(true, "shift-end");
                try {
                  await reconcileCompliance(queryClient);
                } finally {
                  setReconciling(false, "shift-end");
                }
              })();
            }
          }}
          mode="end"
          shiftId={patrolEndFlow.shiftId}
          lockedVehicle={patrolEndFlow.lockedVehicle}
          onRequestEndShift={async (vehicleConditionCheck) => {
            const shiftForGps = readActiveShiftFromCache(queryClient) ?? activeShift;
            const gps = await resolveShiftGpsForSubmit(shiftForGps, "shift_end");
            if (!gps) {
              return;
            }
            const result = await shiftEnd({
              shift_id: patrolEndFlow.shiftId,
              gps_coordinates: gps.gps_coordinates,
              gps_source: gps.gps_source,
              vehicle_condition_check: vehicleConditionCheck,
            });
            shiftEndRevokedRef.current = result.tokens_revoked;
          }}
          onComplete={async () => {
            toast.success("Shift ended successfully");
            setPatrolEndFlow(null);
            setReconciling(true, "shift-end");
            try {
              await reconcileCompliance(queryClient, {shiftId: patrolEndFlow.shiftId});
            } finally {
              setReconciling(false, "shift-end");
            }
            finalizeShiftEndSessionCallback();
          }}
        />
      ) : null}
    </>
  );
}

interface ShiftEndDialogProps {
  open: boolean;
  shift: Shift;
  isShiftingEnded: boolean;
  isExtendingShift: boolean;
  endShiftBusy: boolean;
  showReconcilingOverlay?: boolean;
  actionError?: {message: string; action: ShiftGateAction} | null;
  onClearActionError?: () => void;
  onStay: (extensionMinutes: number) => void;
  onEndOfShift: () => void;
}

function ShiftEndDialog({
  open,
  shift,
  isShiftingEnded,
  isExtendingShift,
  endShiftBusy,
  showReconcilingOverlay,
  actionError,
  onClearActionError,
  onStay,
  onEndOfShift,
}: ShiftEndDialogProps) {
  const toDateTime = moment(shift.extended_end_datetime ?? shift.to_datetime);
  const [showForm, setShowForm] = useState(false);
  const [pendingAction, setPendingAction] = useState<ShiftGateAction | null>(null);
  const {submitBlockedByLocation} = useComplianceGateCoordinates(open, {
    subformOpen: showForm,
  });
  const isBusy = isShiftingEnded || isExtendingShift || endShiftBusy;

  const visibleActionError =
    actionError &&
    (showForm
      ? actionError.action === "stay"
      : actionError.action === "end")
      ? actionError.message
      : null;

  useEffect(() => {
    if (!isBusy) {
      setPendingAction(null);
    }
  }, [isBusy]);

  return (
    <Dialog modal open={open}>
      <DialogContent
        className={complianceGateDialogContentClassName}
        showCloseButton={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-4">
          <ComplianceGateDialogHeader
            icon={Clock}
            title="Shift end"
            description={`Your shift was scheduled to end at ${toDateTime.format("h:mm A")}.`}
          />

          {submitBlockedByLocation ? <CoordinatesRequiredGate /> : null}

          {showForm ? (
            <fieldset disabled={submitBlockedByLocation} className="m-0 border-0 p-0">
              <ShiftEndForm
                loading={isExtendingShift}
                isLoading={pendingAction === "stay"}
                isOtherLoading={isBusy && pendingAction !== "stay"}
                onSubmit={(minutes) => {
                  setPendingAction("stay");
                  void onStay(minutes);
                }}
                onBack={() => {
                  setShowForm(false);
                  onClearActionError?.();
                }}
              />
            </fieldset>
          ) : (
            <fieldset disabled={submitBlockedByLocation} className="m-0 border-0 p-0">
              <div className="flex flex-col gap-4">
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Are you staying past your scheduled end time?
                </p>
                <div className="flex gap-2">
                  <ComplianceGateActionButton
                    variant="outline"
                    className="flex-1"
                    isOtherLoading={isBusy}
                    {...prewarmGateCoordinatesOnPointerDown()}
                    onClick={() => setShowForm(true)}
                  >
                    Stay
                  </ComplianceGateActionButton>
                  <ComplianceGateActionButton
                    className="flex-1"
                    isLoading={pendingAction === "end"}
                    isOtherLoading={isBusy && pendingAction !== "end"}
                    onClick={() => {
                      setPendingAction("end");
                      void onEndOfShift();
                    }}
                  >
                    End shift
                  </ComplianceGateActionButton>
                </div>
              </div>
            </fieldset>
          )}

          {visibleActionError ? (
            <ComplianceGateActionError message={visibleActionError} />
          ) : null}
        </div>
        {showReconcilingOverlay ? <ComplianceReconcileOverlay /> : null}
      </DialogContent>
    </Dialog>
  );
}

interface ShiftEndFormProps {
  loading: boolean;
  isLoading: boolean;
  isOtherLoading: boolean;
  onSubmit: (extensionMinutes: number) => void;
  onBack: () => void;
}

type ShiftEndFormValues = {
  extension_minutes: string;
};

function ShiftEndForm({loading, isLoading, isOtherLoading, onSubmit, onBack}: ShiftEndFormProps) {
  const form = useForm<ShiftEndFormValues>({
    defaultValues: {extension_minutes: ""},
  });
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => {
          const n = Number(data.extension_minutes);
          onSubmit(n);
        })}
      >
        <FormField
          control={form.control}
          name="extension_minutes"
          rules={{
            validate: (v) => {
              if (v === "" || v == null) {
                return "This field is required";
              }
              const n = Number(v);
              if (Number.isNaN(n) || n < 1) {
                return "Must be greater than 0";
              }
              return true;
            },
          }}
          render={({field, formState}) => (
            <FormItem className="max-w-[10rem]">
              <FormLabel className="flex items-end gap-1">
                Extend by <small className="text-muted-foreground">(minutes)</small>
              </FormLabel>
              <FormControl>
                <Input type="number" step="1" min="0" {...field} />
              </FormControl>
              {formState.errors.extension_minutes && (
                <FormError error={formState.errors.extension_minutes.message} />
              )}
            </FormItem>
          )}
        />
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            type="button"
            disabled={loading || isLoading || isOtherLoading}
            onClick={onBack}
          >
            Back
          </Button>
          <ComplianceGateActionButton
            type="submit"
            isLoading={isLoading || loading || form.formState.isSubmitting}
            isOtherLoading={isOtherLoading}
          >
            Stay
          </ComplianceGateActionButton>
        </div>
      </form>
    </Form>
  );
}
